import QueryString from 'qs'
import Request from './Request'
import Scorocode from './Scorocode'
// services
import { Auth } from './services/auth'
import { Client } from './services/client'
import { FS } from './services/fs'
import { PG } from './services/pg'
import { PGManager } from './services/pgManager'
import utils from './utils'
import EventEmitter from './utils/EventEmitter'

export interface ApplicationConfig {
  ssl?: boolean
  host?: string
  port?: number
  client?: {
    ssl?: boolean
    timeout?: number
  }
  auth?: {
    storage: any
  }
  db?: {
    defaultLimit?: number
  }
}

const DEFAULT_APP_CONFIG: ApplicationConfig = {
  host: 'localhost',
  ssl: true,
  client: {
    ssl: true,
    timeout: 120000,
  },
  db: {
    defaultLimit: 20,
  },
}

enum StatusCodes {
  Unauthorized = 401,
  Forbidden = 403,
}

export default class Application extends EventEmitter {
  private readonly _sc: Scorocode
  private readonly _name: string
  private readonly _config: ApplicationConfig
  private _isDestroyed = false
  // services
  private readonly _client: Client
  private readonly _auth: Auth
  private readonly _pg: PG
  private readonly _pgManager: PGManager
  private readonly _fs: FS
  // private readonly _webSocket: any

  constructor(sc: Scorocode, name: string, config: ApplicationConfig) {
    super()

    this._sc = sc
    this._name = name
    this._config = utils.deepExtend({}, DEFAULT_APP_CONFIG, config)
    // services
    this._client = new Client({
      app: this,
    })
    this._auth = new Auth({
      app: this,
      preserveSession: true,
    })
    this._pg = new PG({
      app: this,
    })
    this._pgManager = new PGManager({
      app: this,
    })
    this._fs = new FS({
      app: this,
    })
  }

  get name(): string {
    return this._name
  }

  get config(): ApplicationConfig {
    return this._config
  }

  get scorocode(): Scorocode {
    return this._sc
  }

  // services
  client(): Client {
    this._checkIsAlive()

    return this._client
  }

  auth(): Auth {
    this._checkIsAlive()

    return this._auth
  }

  pg(): PG {
    this._checkIsAlive()

    return this._pg
  }

  pgManager(): PGManager {
    this._checkIsAlive()

    return this._pgManager
  }

  fs(): FS {
    this._checkIsAlive()

    return this._fs
  }

  destroy(): Promise<void> {
    this._checkIsAlive()

    // destroy app
    return (
      new Promise((resolve) => {
        this._checkIsAlive()

        resolve()
      })
        .then(() => this.client().destroy())
        .then(() => this.auth().destroy())
        .then(() => this.pg().destroy())
        .then(() => this.pgManager().destroy())
        .then(() => this.fs().destroy())
        // .then(() => this.webSocket().destroy())
        .then(() => {
          this._isDestroyed = true
        })
    )
  }

  createRequest(requestUri: string): Request {
    this._checkIsAlive()

    const request = new Request({
      url: this.createUrl(requestUri),
    }).use(this._applySession)

    request.on('onRequest', (...args: any[]) => this.emit('onRequest', ...args))
    request.on('onResponseSuccess', (...args: any[]) =>
      this.emit('onResponseSuccess', ...args)
    )
    request.on('onResponseError', (...args: any[]) =>
      this.emit('onResponseError', ...args)
    )

    return request
  }

  createAuthorizedRequest(requestUri: string): Request {
    this._checkIsAlive()

    return this.createRequest(requestUri).use(
      this._applyAccessToken,
      this._refreshTokenFallback
    )
  }

  createAuthenticatedRequest(requestUri: string): Request {
    this._checkIsAlive()

    return this.createRequest(requestUri).use(
      this._applyIdToken,
      this._refreshTokenFallback
    )
  }

  createUrl(requestUri: string, query?: string | {}): string {
    this._checkIsAlive()

    const config = this.config

    let url = `${config.ssl ? 'https' : 'http'}://${config.host +
      (config.port ? `:${config.port}` : '')}${requestUri}`

    if (typeof query === 'string') {
      url += `?${query}`
    } else if (typeof query === 'object') {
      url += `?${QueryString.stringify(query)}`
    }

    return url
  }

  private _checkIsAlive() {
    if (this._isDestroyed) {
      throw utils.throwError(
        'Application.isNotAlive',
        `Application "${this._name}" is destroyed`
      )
    }
  }

  private _applySession = (request: Request) => {
    if (this.auth().currentSession) {
      request.setRequestHeader(
        'X-Session-ID',
        (this.auth() as any).currentSession
      )
    }
  }

  private _applyAccessToken = (request: Request) => {
    if (this.auth().currentUser) {
      request.setRequestHeader(
        'Authorization',
        `${(this.auth() as any).currentUser.credentials.tokenType} ${
          (this.auth() as any).currentUser.credentials.accessToken
        }`
      )
    }
  }

  private _applyIdToken = (request: Request) => {
    if (this.auth().currentUser) {
      request.setRequestHeader(
        'Authorization',
        `Bearer ${(this.auth() as any).currentUser.credentials.idToken}`
      )
    }
  }

  private _refreshTokenFallback = (
    response: Promise<any>,
    request: Request
  ): Promise<any> => {
    return response.catch((error) => {
      if (request.xhr.status === StatusCodes.Unauthorized) {
        if (this.auth().currentUser) {
          return (
            (this.auth() as any).currentUser
              .refresh()
              // если не удалось обновить токен - возвращаем исходную ошибку
              .catch(() => Promise.reject(error))
              // если токен обновлен запускаем запрос по новой и возвращаем его ответ
              .then(() => request.execute())
          )
        }
      }

      return Promise.reject(error)
    })
  }
}

/**
 * Usage
 * const scorocode = require('scorocode')
 * let app = scorocode.initApp({ ...config })
 * app = scorocode.app()
 */
